import { Stack } from '@mui/material';

import CasIconButton from '../CasButton/CasIconButton';
import { CasStackRow } from '../CasStack/CasStackRow';
import CasDisplayField from '../CasDisplayField/CasDisplayField';

// Generic in the item type: the component only ever compares options by identity, so it works for
// the numeric ids it started with and equally for string keys.
interface ICasItemNavigator<T> {
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly options: T[];
  /*
    How to name the current position. Defaults to "3 of 7", which suits a list whose items are all
    the same kind of thing; supply this where they are not, or where the count means something more
    specific to the caller than "how many options there are".
  */
  readonly formatLabel?: (index: number, total: number) => string;
}

export default function CasItemNavigator<T>({
  value,
  onChange,
  options,
  formatLabel,
}: ICasItemNavigator<T>) {
  const currentIndex = options?.findIndex((option) => option === value);

  const handleNext = () => {
    const nextValue = options[currentIndex + 1];
    onChange(nextValue);
  };

  const handlePrevious = () => {
    const prevValue = options[currentIndex - 1];
    onChange(prevValue);
  };

  return (
    <CasStackRow alignItems="center">
      {currentIndex > 0 && (
        <Stack position="relative">
          <CasIconButton icon="raphael:arrowleft" onClick={handlePrevious} />
        </Stack>
      )}
      <CasDisplayField
        label=""
        value={
          formatLabel
            ? formatLabel(currentIndex, options.length)
            : `${currentIndex + 1} of ${options.length}`
        }
      />
      {currentIndex < options.length - 1 && (
        <Stack position="relative">
          <CasIconButton icon="raphael:arrowright" onClick={handleNext} />
        </Stack>
      )}
    </CasStackRow>
  );
}
